import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getCart, addToCart as addToCartUtil, removeFromCart as removeFromCartUtil, updateCartQuantity as updateCartQuantityUtil, updateCartItemSize as updateCartItemSizeUtil, updateCartPricesFromLive, clearCart as clearCartUtil, getCartTotal, getCartCount } from '../utils/cartUtils';
import { api, apiClient } from '../services/api';
import { getActivePushEndpoint } from '../utils/pushNotification';

const CartContext = createContext();
const ABANDONED_CART_DELAY = 10 * 60 * 1000; // 10 dakika (ms)

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const abandonedCartTimerRef = useRef(null);

  // Sepet terk bildirimi gönder
  const sendAbandonedCartNotification = async (cartItems) => {
    if (!cartItems || cartItems.length === 0) return;

    // Bildirim izni yoksa gönderme
    if (!('Notification' in window) || Notification.permission !== 'granted') return;

    try {
      const userId = localStorage.getItem('userId') || null;
      const email = localStorage.getItem('userEmail') ||
                    localStorage.getItem('guestEmail') ||
                    localStorage.getItem('syncedGuestEmail');
      const endpoint = await getActivePushEndpoint();

      // Admin hariç herkese: userId, email veya push endpoint'inden en az biri gerekli
      if (!userId && !email && !endpoint) return;

      const firstProduct = cartItems[0];
      const productName = firstProduct?.name || 'Kıymetli Maden / Altın';

      await apiClient.post('/push/abandoned-cart-reminder', {
        userId,
        email: email || null,
        endpoint: endpoint || null,
        productName,
      });
    } catch {
      /* sessiz */
    }
  };

  // Sepet değişince timer'ı yeniden başlat
  const resetAbandonedCartTimer = (cartItems) => {
    // Önceki timer'ı temizle
    if (abandonedCartTimerRef.current) {
      clearTimeout(abandonedCartTimerRef.current);
      abandonedCartTimerRef.current = null;
    }

    // Sepet boşsa timer başlatma
    if (!cartItems || cartItems.length === 0) return;

    // 10 dakika sonra bildirim gönder
    abandonedCartTimerRef.current = setTimeout(() => {
      sendAbandonedCartNotification(cartItems);
    }, ABANDONED_CART_DELAY);
  };

  useEffect(() => {
    const initialCart = getCart();
    setCart(initialCart);
    // Sayfa yenilenince sepette ürün varsa timer'ı başlat
    resetAbandonedCartTimer(initialCart);

    return () => {
      if (abandonedCartTimerRef.current) {
        clearTimeout(abandonedCartTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const refreshCartPrices = async () => {
      // Sadece o anki güncel sepeti al
      const currentCart = getCart();
      if (!currentCart || currentCart.length === 0) return;
      
      try {
        const ids = currentCart.map(item => item.id).join(',');
        const res = await api.getProducts({ ids });
        const liveProducts = res.data || res;
        
        const newCart = updateCartPricesFromLive(liveProducts);
        
        // Eğer sepet değiştiyse state'i güncelle
        // JSON string karşılaştırması kullanarak gereksiz render'ları önle
        if (JSON.stringify(newCart) !== JSON.stringify(currentCart)) {
          setCart(newCart);
          // Header sayacını resetlemek için event tetikle (döngüye dikkat)
          // Zaten prices:updated eventini dinlediğimiz için sonsuz döngü olabilir!
          // Custom bir 'cart:prices:updated' eventi fırlatmak daha güvenli olabilir,
          // ya da hiç fırlatmayalım, prices:updated Header'da LiveMarkets/ProductDetail tarafından yeterince fırlatılıyor.
        }
      } catch (err) {
        console.error('Failed to sync cart prices', err);
      }
    };

    window.addEventListener('prices:updated', refreshCartPrices);
    const intervalId = setInterval(refreshCartPrices, 30000);
    
    // Uygulama açıldığında ilk senkronizasyonu yap
    refreshCartPrices();
    
    return () => {
      window.removeEventListener('prices:updated', refreshCartPrices);
      clearInterval(intervalId);
    };
  }, []);

  const addToCart = async (product, quantity = 1) => {
    const updatedCart = await addToCartUtil(product, quantity);
    setCart(updatedCart);
    // Sepete ürün eklenince timer'ı yeniden başlat
    resetAbandonedCartTimer(updatedCart);
  };

  const removeFromCart = (productId) => {
    const updatedCart = removeFromCartUtil(productId);
    setCart(updatedCart);
    // Ürün çıkarılınca timer'ı güncelle (sepet boşaldıysa iptal edilir)
    resetAbandonedCartTimer(updatedCart);
  };

  const updateCartQuantity = async (productId, quantity) => {
    const updatedCart = await updateCartQuantityUtil(productId, quantity);
    setCart(updatedCart);
    resetAbandonedCartTimer(updatedCart);
  };

  const clearCart = () => {
    const updatedCart = clearCartUtil();
    setCart(updatedCart);
    // Sepet temizlenince timer'ı iptal et (sipariş verildi)
    if (abandonedCartTimerRef.current) {
      clearTimeout(abandonedCartTimerRef.current);
      abandonedCartTimerRef.current = null;
    }
  };

  const updateCartItemSize = (cartKey, newSize) => {
    const updatedCart = updateCartItemSizeUtil(cartKey, newSize);
    setCart(updatedCart);
  };

  const [paymentMethod, setPaymentMethod] = useState(() => {
    if (typeof window === 'undefined') return 'CREDIT_CARD';
    const saved = localStorage.getItem('checkoutPaymentMethod');
    return saved === 'HAVALE' ? 'HAVALE' : 'CREDIT_CARD';
  });
  const [deliveryType, setDeliveryType] = useState('CARGO');

  useEffect(() => {
    localStorage.setItem('checkoutPaymentMethod', paymentMethod);
  }, [paymentMethod]);

  const cartTotal = cart.reduce((total, item) => {
    const itemPrice = item.creditCardPrice || (item.basePrice ? Number(item.basePrice) * 1.05 : Number(item.price || 0) * 1.05);
    return total + (parseFloat(itemPrice) * item.quantity);
  }, 0);
  
  const havaleCartTotal = cart.reduce((total, item) => {
    const itemPrice = item.havalePrice || item.basePrice || item.price || 0;
    return total + (parseFloat(itemPrice) * item.quantity);
  }, 0);

  const activeCartTotal = paymentMethod === 'HAVALE' ? havaleCartTotal : cartTotal;
  const cartCount = cart.reduce((count, item) => count + item.quantity, 0);

  const contextValue = React.useMemo(() => ({
    cart,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    updateCartItemSize,
    clearCart,
    cartTotal,
    havaleCartTotal,
    activeCartTotal,
    paymentMethod,
    setPaymentMethod,
    deliveryType,
    setDeliveryType,
    cartCount,
    isCartOpen,
    setIsCartOpen
  }), [
    cart, cartTotal, havaleCartTotal, activeCartTotal, 
    paymentMethod, deliveryType, cartCount, isCartOpen
  ]);

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};
